import { isFeatureEnabledWithFallback } from '@/lib/featureEntitlements';
import {
  isIntegrationEntitled,
  type IntegrationEntitlementContext,
} from '@/lib/integrationEntitlements';
import { isIntegrationGrantedByPermissions } from '@/lib/integrationPermissions';
import {
  ADS_CONNECT_HREF,
  COMMERCE_CONNECT_HREF,
  SOCIAL_CONNECT_HREF,
} from '@/lib/dashboard/connectHrefs';
import { WHATSAPP_BUSINESS_INTEGRATION_URL } from '@/lib/whatsapp/businessAccountRoute';


export type SourceState = 'ok' | 'not_entitled' | 'not_connected' | 'unknown';

export type CapabilityState = 'ok' | 'not_entitled';

export interface DashboardSourceDef {
  key: string;
  label: string;
  featureKey?: string;
  integrationKey?: string;
  needsConnection: boolean;
  connectHref?: string;
  connectAction?: 'meta-signup';
}

export const DASHBOARD_SOURCES: Record<string, DashboardSourceDef> = {
  whatsapp: {
    key: 'whatsapp',
    label: 'WhatsApp',
    featureKey: 'inbox',
    needsConnection: true,
    connectHref: WHATSAPP_BUSINESS_INTEGRATION_URL,
    connectAction: 'meta-signup',
  },
  instagram: {
    key: 'instagram',
    label: 'Instagram',
    featureKey: 'inbox',
    integrationKey: 'instagram',
    needsConnection: true,
    connectHref: SOCIAL_CONNECT_HREF,
  },
  facebook: {
    key: 'facebook',
    label: 'Facebook',
    featureKey: 'inbox',
    integrationKey: 'facebook',
    needsConnection: true,
    connectHref: SOCIAL_CONNECT_HREF,
  },

  nativeOrders: {
    key: 'nativeOrders',
    label: 'WhatsApp catalog',
    featureKey: 'orders',
    needsConnection: false,
  },
  shopify: {
    key: 'shopify',
    label: 'Shopify',
    featureKey: 'orders',
    integrationKey: 'shopify',
    needsConnection: true,
    connectHref: COMMERCE_CONNECT_HREF,
  },
  woocommerce: {
    key: 'woocommerce',
    label: 'WooCommerce',
    featureKey: 'orders',
    integrationKey: 'woocommerce',
    needsConnection: true,
    connectHref: COMMERCE_CONNECT_HREF,
  },
  petpooja: {
    key: 'petpooja',
    label: 'PetPooja',
    featureKey: 'orders',
    integrationKey: 'petpooja',
    needsConnection: true,
    connectHref: COMMERCE_CONNECT_HREF,
  },

  instagramPosts: {
    key: 'instagramPosts',
    label: 'Instagram',
    featureKey: 'social_organic_posts',
    integrationKey: 'instagram',
    needsConnection: true,
    connectHref: SOCIAL_CONNECT_HREF,
  },
  facebookPosts: {
    key: 'facebookPosts',
    label: 'Facebook',
    featureKey: 'social_organic_posts',
    integrationKey: 'facebook',
    needsConnection: true,
    connectHref: SOCIAL_CONNECT_HREF,
  },
  linkedinPosts: {
    key: 'linkedinPosts',
    label: 'LinkedIn',
    featureKey: 'social_organic_posts',
    integrationKey: 'linkedin',
    needsConnection: true,
    connectHref: SOCIAL_CONNECT_HREF,
  },
  youtubePosts: {
    key: 'youtubePosts',
    label: 'YouTube',
    featureKey: 'social_organic_posts',
    integrationKey: 'youtube',
    needsConnection: true,
    connectHref: SOCIAL_CONNECT_HREF,
  },

  scheduling: {
    key: 'scheduling',
    label: 'Scheduling',
    featureKey: 'appointments',
    needsConnection: false,
  },
  crm: { key: 'crm', label: 'CRM', featureKey: 'contacts', needsConnection: false },
  // Money received on CRM deals — a Revenue source alongside the stores, so a plan without
  // Commerce still sees its deal revenue. Same feature gate as the CRM source above.
  deals: { key: 'deals', label: 'Deals', featureKey: 'contacts', needsConnection: false },
  automations: {
    key: 'automations',
    label: 'Automations',
    featureKey: 'automations',
    needsConnection: false,
  },
  campaigns: {
    key: 'campaigns',
    label: 'Campaigns',
    featureKey: 'campaigns',
    needsConnection: false,
  },

  metaAds: {
    key: 'metaAds',
    label: 'Meta',
    featureKey: 'meta_ads',
    needsConnection: false,
    connectHref: ADS_CONNECT_HREF,
  },
  googleAds: {
    key: 'googleAds',
    label: 'Google Ads',
    featureKey: 'google_ads',
    needsConnection: false,
    connectHref: ADS_CONNECT_HREF,
  },
  linkedinAds: {
    key: 'linkedinAds',
    label: 'LinkedIn Ads',
    featureKey: 'linkedin_ads',
    needsConnection: false,
    connectHref: ADS_CONNECT_HREF,
  },
};

export type DashboardSourceKey = keyof typeof DASHBOARD_SOURCES;

export interface CapabilityInput {
  hasEntitlements: boolean;
  features: Record<string, unknown> | null | undefined;
  subFeatures: Record<string, unknown> | null | undefined;
  limits?: Record<string, unknown> | null | undefined;
  menuPermissions?: string[] | null;
  connections?: Record<string, boolean | null | undefined>;
}

export interface DashboardCapability {
  state: CapabilityState;
  fetchable: string[];
  entitled: string[];
  connected: string[];
  needsConnection: DashboardSourceDef[];
  bySource: Record<string, SourceState>;
}

export function resolveDashboardCapability(
  sourceKeys: readonly string[],
  input: CapabilityInput
): DashboardCapability {
  const ctx: IntegrationEntitlementContext = {
    hasEntitlements: input.hasEntitlements,
    features: input.features,
    subFeatures: input.subFeatures,
    limits: input.limits,
  };

  const defs = sourceKeys
    .map((k) => DASHBOARD_SOURCES[k])
    .filter((d): d is DashboardSourceDef => Boolean(d));

  const bySource: Record<string, SourceState> = {};

  for (const def of defs) {
    const featureOk =
      !input.hasEntitlements ||
      isFeatureEnabledWithFallback(
        input.features as Record<string, boolean> | null | undefined,
        def.featureKey
      );
    if (!featureOk) {
      bySource[def.key] = 'not_entitled';
      continue;
    }

    if (def.integrationKey && !isIntegrationEntitled(ctx, def.integrationKey)) {
      bySource[def.key] = 'not_entitled';
      continue;
    }

    if (
      def.integrationKey &&
      !isIntegrationGrantedByPermissions({
        menu_permissions: input.menuPermissions ?? null,
        entitlementKey: def.integrationKey,
      })
    ) {
      bySource[def.key] = 'not_entitled';
      continue;
    }

    if (!def.needsConnection) {
      bySource[def.key] = 'ok';
      continue;
    }
    const conn = input.connections?.[def.key];
    if (conn === false) {
      bySource[def.key] = 'not_connected';
      continue;
    }
    bySource[def.key] = conn === true ? 'ok' : 'unknown';
  }

  const entitled = defs.filter((d) => bySource[d.key] !== 'not_entitled');
  const fetchable = entitled;
  const connected = entitled.filter((d) => bySource[d.key] === 'ok');
  const needsConnection = entitled.filter((d) => bySource[d.key] === 'not_connected');

  const state: CapabilityState = entitled.length === 0 ? 'not_entitled' : 'ok';

  return {
    state,
    fetchable: fetchable.map((d) => d.key),
    entitled: entitled.map((d) => d.key),
    connected: connected.map((d) => d.key),
    needsConnection,
    bySource,
  };
}

export function shouldOfferConnect(capability: DashboardCapability): boolean {
  if (capability.needsConnection.length === 0) return false;
  return !capability.connected.some((key) => DASHBOARD_SOURCES[key]?.needsConnection);
}

export function listSourceLabels(defs: readonly DashboardSourceDef[]): string {
  return joinLabels(defs.map((d) => d.label));
}

export function joinLabels(names: readonly string[]): string {
  const clean = names.filter((n) => typeof n === 'string' && n.trim().length > 0);
  if (clean.length === 0) return '';
  if (clean.length === 1) return clean[0];
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}
